// --------------------------------------------------------------
// Created On: 2025-04-15
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useRef } from "react";

// This Button component is used to upload a CSV file and process its contents.
export default function ImportCsvButton(props: Props): Component {
  const uploadRef = useRef<HTMLInputElement>(null);

  // This function filters and validates the headers of the CSV file.
  function filterAndValidateHeaders(headers: string[]): string[] {
    const validHeaders = ["part_number", "quantity", "impeller_trim_inches", "impeller_angle_degrees", "paint_color"];
    const filteredHeaders: string[] = headers.filter((item) => item !== "notes");
    const allHeadersValid: boolean = validHeaders.every((header) => filteredHeaders.includes(header));
    if (allHeadersValid) {
      return filteredHeaders;
    } else {
      return [];
    }
  }

  // This function formats the headers of the CSV file.
  function formatHeader(header: string): string {
    switch (header) {
      case "part_number":
        return "partNumber";
      case "quantity":
        return "quantity";
      case "impeller_trim_inches":
        return "impellerTrim";
      case "impeller_angle_degrees":
        return "impellerAngle";
      case "paint_color":
        return "paintColor";
      default:
        // If the header is not recognized, return an empty string.
        return "";
    }
  }

  // Calculate trim option and value.
  function calculateTrimOptionAndValue(trimValue: string | null): string {
    let trimOption = "";
    if (trimValue === null || trimValue === "") {
      trimOption = "";
    } else if (trimValue.toLowerCase() === "full trim") {
      trimOption = "full-trim";
    } else if (typeof parseInt(trimValue) === "number") {
      trimOption = "custom";
    }
    return trimOption;
  }

  // This function is called when the CSV file is loaded and the contents are read.
  // It processes the contents of the CSV file and stores the data in an array of objects.
  // The function also formats the headers and retrieves the data rows from the CSV file.
  function csvOnLoad(event: ProgressEvent<FileReader>) {
    const contents = event.target?.result;
    if (contents !== null && contents !== undefined) {
      // Initialize an empty array to store the parts.
      const uploadedParts: UploadedPart[] = [];
      const dataRows: string[][] = [];

      // Create and format the headers and data rows from the CSV file.
      const csvData = contents.toString().replace("\r", "").split("\n");
      csvData.forEach((row) => {
        const dataRow = [...row.split(",")];
        dataRows.push(dataRow);
      });
      // Filter out the headers that are not needed and validate the remaining headers.
      const headers = filterAndValidateHeaders(dataRows[0]);
      // If the headers are empty, show an error message.
      if (headers.length === 0) {
        props.onErrorMessageChange("Invalid headers in the CSV file.");
        return;
      }
      const formattedHeaders: string[] = [];
      // Format the headers and check if they are valid.
      for (const header of headers) {
        const formattedHeader = formatHeader(header);
        if (formattedHeader === "") {
          props.onErrorMessageChange(`Invalid header: ${header}`);
          return;
        } else {
          formattedHeaders.push(formattedHeader);
        }
      }

      // Retrieve the data rows from the CSV file, skipping the first row, containing the headers.
      const rows = dataRows.slice(1);
      for (const row of rows) {
        // Check if the row is not empty.
        // Checking for a row length greater than 1 to ensure it contains data.
        //  An empty row ( [""] ) would have a length of 1.
        if (row.length > 1) {
          // If the row is not empty, split it into an array of values.
          const singleDataRow = row.filter((column) => column !== undefined && column !== null);

          // Initialize an empty object to store the uploaded part.
          // This object will be used to store the data for each part.
          let uploadedPart: UploadedPart = {
            partNumber: "",
            description: "",
            quantity: "",
            paintColor: "",
            impellerTrim: "0.0",
            impellerAngle: "0",
          };

          // Get the value for each header.
          formattedHeaders.forEach((header, index) => {
            const values = singleDataRow;
            const value = values[index] === undefined ? "" : values[index];
            // Use the header as the key and assign the value to the uploadedPart object.
            uploadedPart = {
              ...uploadedPart,
              [header]: value,
            };
          });
          if (uploadedPart.partNumber !== "") {
            // Add the uploaded part to the array.
            uploadedParts.push(uploadedPart);
          }
        }
      }

      // Create an array of parts with the additionally needed information for rendering the parts list in the form.
      const uploadedPartsList: Part[] = [];
      for (const part of uploadedParts) {
        const uploadedPart: Part = {
          partId: 0,
          partNumber: part.partNumber,
          description: part.description,
          quantity: part.quantity,
          unitName: "",
          price: 0,
          companyDiscount: null,
          weight: null,
          paintColorId:
            part.paintColor !== ""
              ? getPaintColorIdByName(part.paintColor, props.paintColors)
              : props.defaultPaintColorId,
          partNumberIsInvalid: false,
          invalidPartNumberMessage: "",
          isSellable: true,
          isPaintable: part.paintColor !== "" ? true : false,
          isImpeller: part.impellerAngle !== "" && part.impellerTrim !== "" ? true : false,
          isFullTrim: part.impellerTrim.toLowerCase() === "full trim" ? true : false,
          impellerTrimOption: calculateTrimOptionAndValue(part.impellerTrim),
          impellerTrimValue: part.impellerTrim.toLowerCase() !== "full trim" ? part.impellerTrim : "0.0",
          impellerAngle: part.impellerAngle,
        };
        uploadedPartsList.push(uploadedPart);
      }

      props.onUploadedPartsListChange(uploadedPartsList);
    } else {
      props.onErrorMessageChange("File is empty or could not be read.");
      return;
    }
  }

  // This function handles the file upload and processes the CSV file by reading the file contents, parsing the CSV data, and storing it in an array of objects.
  function handleUpload(event: React.ChangeEvent<HTMLInputElement>) {
    // Check if the file input is empty or if no files were selected.
    // If the file input is empty, show an error message.
    if (event.target.files === null || event.target.files.length === 0) {
      props.onErrorMessageChange("Please select a file to upload.");
      return;
    }
    // Get the first file from the input element.
    const file = event.target.files[0];
    // Check if the file is not null or undefined.
    // If the file is null or undefined, show an error message.
    if (file !== null && file !== undefined) {
      // Check if the file type is CSV
      // If the file type is not CSV, show an error message.
      // If the file type is CSV, read its contents and process it.
      if (!file.name.endsWith(".csv")) {
        props.onErrorMessageChange("Please select a valid CSV file.");
        return;
      }
      const fileReader = new FileReader();
      fileReader.onload = csvOnLoad;

      // Clear the input value to allow re-uploading the same file
      event.target.value = "";
      // Read the file as text
      fileReader.readAsText(file);
    } else {
      props.onErrorMessageChange("File could not be uploaded. Please try again.");
    }
  }

  // This function retrieves the paint color ID by name from the paintColors array.
  function getPaintColorIdByName(name: string, paintColors: PaintColor[]): number | null {
    const paintColor = paintColors.find((color) => color.name.toLowerCase() === name.toLowerCase());
    if (paintColor) {
      return paintColor.paintColorId;
    } else {
      return null;
    }
  }

  return (
    <Fragment>
      <button
        data-test="quick-order-page-import-csv-button"
        type="submit"
        className="col col-lg-4 btn btn-secondary w-25 mx-2"
        onClick={() => uploadRef.current?.click()}
      >
        {props.buttonText}
      </button>

      <input type="file" ref={uploadRef} className="d-none" accept=".csv" onChange={(e) => handleUpload(e)} />
    </Fragment>
  );
}

interface Props {
  buttonText: string;
  defaultPaintColorId: number | null;
  paintColors: PaintColor[];
  onUploadedPartsListChange: (partsList: Part[]) => void;
  onErrorMessageChange: (message: string) => void;
}

interface UploadedPart {
  partNumber: string;
  description: string;
  quantity: string;
  paintColor: string;
  impellerTrim: string;
  impellerAngle: string;
}

interface Part {
  partId: number;
  partNumber: string;
  description: string;
  quantity: string;
  unitName: string;
  price: number;
  companyDiscount: number | null;
  weight: number | null;
  paintColorId: number | null;
  partNumberIsInvalid: boolean;
  isSellable: boolean;
  isPaintable: boolean;
  isImpeller: boolean;
  isFullTrim: boolean;
  impellerTrimOption: string;
  impellerTrimValue: string | null;
  impellerAngle: string | null;
  invalidPartNumberMessage: string;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}
